import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import { sentryError } from 'utils';
import { SIZE } from 'models/Size';
import { Loader } from 'ncoded-component-library';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DocumentAnalytics } from 'router/subrouters/DataRoom/components/DocumentAnalytics';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import ResolvePagination from 'components/ResolvePagination';
import ShareableLinkFile from './components/ShareableLinkFile';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  SORTING_VALUES,
  SORTING_VALUES_COPY,
  SORTING_VALUES_ICON,
  SortingValues,
} from './data';
import { DataRoomShareableLinkNodeReport } from 'models/ShareableLink';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { useTranslation } from 'react-i18next';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';

import './ShareableLinkFiles.styles.scss';
import './ShareableLinkFiles.styles.responsive.scss';

const LIMIT = '10';
const DEFAULT_SORT = `-${SORTING_VALUES.NUMBER_OF_VIEWS}`;
const PAGE_FILES = 'pageFiles';

type ShareableLinkFilesProps = {
  className?: string;
  shareableLinkId: string;
};

const ShareableLinkFiles: React.FC<ShareableLinkFilesProps> = (props) => {
  const { className, shareableLinkId } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [nodes, setNodes] = useState<DataRoomShareableLinkNodeReport[]>([]);

  const [selectedAnalytics, setSelectedAnalytics] = useState('');

  const { openNode } = useContext(DocumentViewContext);
  const {
    dataRoom: { name: dataRoomName },
  } = useContext(DataRoomContext);

  const { params, setQueryParam } = useQueryParams();

  const pageFiles = usePageParams(PAGE_FILES);
  const sortFiles = params?.sortFiles || DEFAULT_SORT;

  const isEmpty = useMemo(() => !nodes?.length, [nodes?.length]);

  const classes = classNames(
    'shareable-link-files',
    { loading: isLoading && isEmpty },
    className,
  );

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const toggleAnalytics = useCallback(
    (dataRoomNodeId: string) => {
      if (selectedAnalytics === dataRoomNodeId) setSelectedAnalytics('');
      else setSelectedAnalytics(dataRoomNodeId);
    },
    [selectedAnalytics],
  );

  const renderSortButton = useCallback(
    (sortName: SortingValues) => {
      const copy = SORTING_VALUES_COPY[sortName];
      const Icon = SORTING_VALUES_ICON[sortName];

      return (
        <SortingButton
          ascDefault={false}
          sortingValue={sortName}
          sort={sortFiles}
          updateSort={(sort) => setQueryParam('sortFiles', sort)}
          content={
            <>
              {t(copy)} <Icon />
            </>
          }
        />
      );
    },
    [sortFiles, t, setQueryParam],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items, totalPages },
        } = await dataRoomApi.shareableLinks.getDataRoomNodes(shareableLinkId, {
          $page: pageFiles,
          $limit: LIMIT,
          $sort: sortFiles,
        });

        setNodes(items);
        setTotalPages(totalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shareableLinkId, pageFiles, sortFiles]);

  return (
    <div className={classes}>
      {isLoading && isEmpty ? (
        <Loader />
      ) : isEmpty ? (
        <ReportsEmptyState size={isPhablet ? SIZE.SMALL : SIZE.BIG} />
      ) : (
        <>
          <div className="sorting-buttons">
            {renderSortButton(SORTING_VALUES.NAME)}
            {renderSortButton(SORTING_VALUES.NUMBER_OF_VIEWS)}
            {renderSortButton(SORTING_VALUES.NUMBER_OF_DOWNLOADS)}
            <span>{t('size')}</span>
            {renderSortButton(SORTING_VALUES.LAST_ACCESS)}
          </div>

          <div className="shareable-link-files__files">
            {nodes.map((node, index) => {
              const {
                dataRoomNode,
                numberOfDownloads,
                numberOfViews,
                lastAccessDate,
              } = node;

              const isOpen = dataRoomNode._id === selectedAnalytics;
              const isLastChild = index === nodes.length - 1;

              return (
                <div
                  key={dataRoomNode._id}
                  className={classNames('shareable-link-files__file', {
                    open: isOpen,
                    last: isLastChild,
                  })}
                >
                  <ShareableLinkFile
                    dataRoomNode={dataRoomNode}
                    numberOfViews={numberOfViews}
                    numberOfDownloads={numberOfDownloads}
                    lastAccessDate={lastAccessDate}
                    openFile={() => openNode(dataRoomNode, dataRoomName)}
                    onClick={() => toggleAnalytics(dataRoomNode._id)}
                  />
                  <DocumentAnalytics
                    node={dataRoomNode}
                    isOpen={isOpen}
                    getDocumentAnalytics={(dataRoomNodeId, version) =>
                      dataRoomApi.shareableLinks.getDataRoomNodeAnalytics(
                        shareableLinkId,
                        dataRoomNodeId,
                        version,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>

          <ResolvePagination totalPages={totalPages} pageParam={PAGE_FILES} />
        </>
      )}

      {isLoading && !isEmpty && <Loader />}
    </div>
  );
};

export default ShareableLinkFiles;
